import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "fitsme.access_token";
const GUEST_WISHLIST_KEY = "fitsme_wishlist_guest_v1";

const _mergeGuestWishlist = async () => {
    try {
        const raw = localStorage.getItem(GUEST_WISHLIST_KEY);
        if (!raw) return;
        const ids = JSON.parse(raw);
        if (!Array.isArray(ids) || ids.length === 0) return;
        const { data } = await api.post("/wishlist/merge", { product_ids: ids });
        localStorage.removeItem(GUEST_WISHLIST_KEY);
        if (data?.added > 0) {
            toast.success(`Synced ${data.added} saved fit${data.added > 1 ? "s" : ""} to your account`);
        }
        window.dispatchEvent(new CustomEvent("fitsme:wishlist-changed"));
    } catch (err) {
        // Non-fatal — keep guest list intact so user can retry later.
        console.debug("guest wishlist merge failed:", err?.message);
    }
};

const AuthContext = createContext({
    user: null,
    token: null,
    loading: true,
    requestMagicLink: async () => ({}),
    verifyMagicLink: async () => ({}),
    logout: () => {},
    linkProfile: async () => {},
});

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Attach token to every axios request
    useEffect(() => {
        const id = api.interceptors.request.use((config) => {
            const t = localStorage.getItem(STORAGE_KEY);
            if (t) config.headers.Authorization = `Bearer ${t}`;
            return config;
        });
        return () => api.interceptors.request.eject(id);
    }, []);

    // Bootstrap: if we have a token, fetch the user
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            try {
                const { data } = await api.get("/auth/me");
                if (!cancelled) setUser(data);
            } catch (err) {
                console.debug("/auth/me failed — dropping stale token:", err?.message);
                if (!cancelled) {
                    localStorage.removeItem(STORAGE_KEY);
                    setToken(null);
                    setUser(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const requestMagicLink = useCallback(async (email, nextPath = "/") => {
        const { data } = await api.post("/auth/magic-link/request", { email, next: nextPath });
        return data;  // { ok, dev_link?, mocked, ... }
    }, []);

    const verifyMagicLink = useCallback(async (magicToken) => {
        const { data } = await api.get(`/auth/magic-link/verify`, { params: { token: magicToken } });
        localStorage.setItem(STORAGE_KEY, data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        _mergeGuestWishlist();
        window.dispatchEvent(new CustomEvent("fitsme:signed-in", { detail: data.user }));
        return data;
    }, []);

    const exchangeGoogleSession = useCallback(async (sessionId) => {
        const { data } = await api.post("/auth/google/exchange", { session_id: sessionId });
        localStorage.setItem(STORAGE_KEY, data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        _mergeGuestWishlist();
        window.dispatchEvent(new CustomEvent("fitsme:signed-in", { detail: data.user }));
        return data;
    }, []);

    const logout = useCallback(async () => {
        // Call the backend so it can clear the httpOnly fitsme_session cookie.
        // If the request fails (offline / 4xx), still clear local state — the
        // user clicked sign-out, they expect to be signed out.
        try {
            await api.post("/auth/logout");
        } catch (err) {
            console.debug("backend logout failed (clearing client state anyway):", err?.message);
        }
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        if (!localStorage.getItem(STORAGE_KEY)) return null;
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (err) {
            console.debug("refreshUser failed:", err?.message);
            return null;
        }
    }, []);

    const linkProfile = useCallback(async (profileId) => {
        const { data } = await api.post("/auth/me/link-profile", { profile_id: profileId });
        setUser(data);
        return data;
    }, []);

    const value = useMemo(
        () => ({ user, token, loading, requestMagicLink, verifyMagicLink, exchangeGoogleSession, logout, linkProfile, refreshUser }),
        [user, token, loading, requestMagicLink, verifyMagicLink, exchangeGoogleSession, logout, linkProfile, refreshUser],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export const useIsAdmin = () => {
    const { user } = useAuth();
    return !!user?.is_admin;
};
